import { computed } from 'vue';
import type { RouteMeta } from 'vue-router';

import { GlobalState } from '@/global';
import type { IZynoPermissions } from '@signal24/zyno-shared';

export function makePermissionMeta<A extends keyof IZynoPermissions, F extends keyof NonNullable<IZynoPermissions[A]>>(
    app: A,
    feature?: F | (() => F)
): RouteMeta {
    return { app, feature: feature as string | string | (() => string) | undefined };
}

export function hasPermission<A extends keyof IZynoPermissions, F extends keyof NonNullable<IZynoPermissions[A]>>(
    app: A,
    feature?: F | (() => F)
): boolean {
    if (!GlobalState.permissions.value) return false;
    const appPermissions = GlobalState.permissions.value[app];
    if (!appPermissions) return false;
    if (!feature) return true;

    feature = typeof feature === 'function' ? feature() : feature;
    return appPermissions![feature] === true;
}

export const routesWithAuthorization = computed(() => {
    const result = GlobalState.router
        .getRoutes()
        // .filter(r => r.children.length === 0) /* child routes contain the permissions */
        .map(r => ({
            path: r.path,
            pathRe: new RegExp(`^${r.path.replace(/:[^/]+/g, '.+')}$`),
            redirect: r.redirect,
            authorized: r.redirect
                ? null
                : r.meta.admin
                  ? GlobalState.tenant.value?.isAdmin
                  : // eslint-disable-next-line no-explicit-any
                    r.meta.app === undefined || hasPermission(r.meta.app as any, r.meta.feature as any)
        }));
    // biome-ignore lint/complexity/noForEach: <explanation>
    result
        .filter(r => r.redirect)
        .forEach(r => {
            r.authorized = result.find(o => o.path === r.redirect)?.authorized ?? false;
        });
    return result;
});

export function hasRouteAccess(path: string) {
    const matchingRoute = routesWithAuthorization.value.find(r => r.pathRe.test(path));
    return matchingRoute?.authorized === true;
}
